/**
 * Einstellungen — die **Anlage**, auf die sich die Seitenleiste bezieht.
 *
 * Seit HM-162 folgt die Seite dem überarbeiteten Entwurf
 * (`ui_kits/dashboard/SettingsScreen.jsx`): Abschnitte mit der Überschrift über
 * einer Karte, darin Zeilen mit der Beschriftung links und dem Feld rechts.
 *
 * 1. **Allgemein** — Name, Art, Zeitzone, Haushalt. Die Zeitzone ist die
 *    folgenreichste Angabe des ganzen Produkts: An ihr hängt jede Tagesgrenze.
 * 2. **Anschrift** — vollständig freiwillig; die Koordinaten eingeklappt, weil
 *    die Anschrift fast immer reicht. Zahlen, keine Karte: Ein Kartenbild lädt
 *    Kacheln von einem fremden Server, samt IP-Adresse des Nutzers.
 * 3. **Zugriff** — mit wem die Anlage geteilt ist (HM-160); ein Gast sieht hier,
 *    wem sie gehört.
 * 4. **Geräte** — wie viele, und der Weg in die Geräteverwaltung.
 * 5. **Anlage verwalten** — Archivieren mit Rückfrage; nur der Besitzer.
 *
 * **Ein Speichern für Allgemein und Anschrift zusammen.** Die Leiste unten
 * erscheint erst, wenn sich etwas geändert hat, und bietet Verwerfen und
 * Speichern; vorher speicherte jede Karte für sich, und eine geänderte Anschrift
 * blieb ungespeichert, wenn man unter „Stammdaten" auf Speichern drückte. Teilen
 * und Archivieren wirken sofort und haben deshalb eigene Knöpfe. Die Rechnung
 * dahinter steht in `lib/site-settings-form.ts`.
 *
 * **Übernommen ist der Aufbau, nicht jede Zusage.** Der Archivieren-Dialog des
 * Entwurfs verspricht „später wiederherstellen" — das gibt es nicht. Und die
 * Stufe einer Teilung bleibt eine Auswahl statt einer Marke: Sie zu ändern ist
 * eine Funktion, die es gibt.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Dialog, EmptyState, Input, Select, icons } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { useDevices } from "../lib/queries.js";
import { useSiteAccess } from "../lib/site-access.js";
import { SITE_KIND_LABELS } from "../lib/site-kind.js";
import {
  COUNTRIES,
  coordinateSummary,
  formDirty,
  formErrors,
  optionsWith,
  siteForm,
  sitePatch,
  TIME_ZONES,
  type SiteForm,
} from "../lib/site-settings-form.js";
import { SiteKindField } from "../components/site-kind-field.js";
import { FieldRow, SettingsSection } from "../components/settings-section.js";
import { SharedWithMeSection, SiteSharesSection } from "../components/site-shares.js";

type Site = ApiComponents["schemas"]["Site"];

export interface SettingsScreenProps {
  readonly site: Site | null;
  /** Führt in die Geräteverwaltung. Fehlt er, bleibt die Zeile ohne Weg. */
  readonly onOpenDevices?: () => void;
}

export function SettingsScreen({ site, onOpenDevices }: SettingsScreenProps) {
  if (site === null) {
    return (
      <Card title="Einstellungen">
        <EmptyState title="Keine Anlage gewählt" icon={<icons.IconBuilding size={30} />}>
          Diese Seite zeigt die Einstellungen einer Anlage. Über die Schaltfläche oben in
          der Seitenleiste legst du die erste an.
        </EmptyState>
      </Card>
    );
  }

  /**
   * `key` auf der Kennung: Wer die Anlage wechselt, bekommt frische
   * Formularzustände. Ohne das stünden im Formular noch die Felder der zuvor
   * gewählten Anlage — und „Speichern" schriebe sie in die neue.
   */
  return <SiteSettings key={site.id} site={site} onOpenDevices={onOpenDevices} />;
}

function SiteSettings({ site, onOpenDevices }: { site: Site; onOpenDevices?: () => void }) {
  const queryClient = useQueryClient();
  /** Was die Teilung nicht erlaubt, fehlt (HM-160): mit „Lesen" gesperrte Felder, keine Leiste. */
  const rights = useSiteAccess();
  const readOnly = !rights.canWrite;
  const devices = useDevices(site.id);

  const [saved, setSaved] = useState<SiteForm>(() => siteForm(site));
  const [form, setForm] = useState<SiteForm>(saved);
  const [geoOpen, setGeoOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const errors = formErrors(form);
  const dirty = !readOnly && formDirty(form, saved);
  const patch = sitePatch(form);

  useEffect(() => {
    if (toast === null) return;
    const timer = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(timer);
  }, [toast]);

  const save = useMutation({
    mutationFn: async () => {
      if (patch === null) throw new Error("Bitte erst die markierten Felder berichtigen.");
      const { data, error: failed } = await api.PATCH("/sites/{siteId}", {
        params: { path: { siteId: site.id } },
        body: patch,
      });
      // Die Meldung kommt vom Server — etwa bei unbekannter Zeitzone.
      if (failed || !data) throw new Error(apiErrorMessage(failed) ?? "Speichern fehlgeschlagen.");
      return data;
    },
    onSuccess: async (updated) => {
      /* Was der Server gespeichert hat, ist der neue Ausgangspunkt — getrimmt,
         Land in Großbuchstaben. Sonst bliebe „ungespeichert" stehen. */
      const next = siteForm(updated);
      setSaved(next);
      setForm(next);
      setGeoOpen(false);
      setError(null);
      setToast("Gespeichert.");
      await queryClient.invalidateQueries({ queryKey: ["sites"] });
      await queryClient.invalidateQueries({ queryKey: ["me", "shared-sites"] });
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const set =
    <K extends keyof SiteForm>(key: K) =>
    (value: SiteForm[K]) =>
      setForm((current) => ({ ...current, [key]: value }));
  const pair = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))", gap: 12 } as const;
  const coordinates = coordinateSummary(form.latitude, form.longitude);
  /** Die Felder bleiben offen, solange eines einen Fehler trägt — sonst verschwände er mit ihnen. */
  const showCoordinateFields = geoOpen || errors.latitude !== undefined || errors.longitude !== undefined;
  const deviceCount = devices.data?.items.length ?? 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26, maxWidth: 1100, paddingBottom: dirty ? 72 : 0 }}>
      <SettingsSection title="Allgemein">
        <FieldRow label="Name" hint="So heißt die Anlage im Umschalter.">
          <Input
            label="Name"
            labelHidden
            value={form.name}
            maxLength={120}
            disabled={readOnly}
            error={errors.name}
            onChange={(event) => set("name")(event.target.value)}
          />
        </FieldRow>
        <FieldRow label="Art der Anlage">
          {readOnly ? (
            <span style={{ display: "inline-flex", alignItems: "center", height: 38, fontSize: 13.5, color: "var(--text-strong)" }}>
              {SITE_KIND_LABELS[form.kind]}
            </span>
          ) : (
            <SiteKindField value={form.kind} onChange={set("kind")} />
          )}
        </FieldRow>
        <FieldRow label="Zeitzone" hint="Jeder Tag beginnt um Mitternacht in dieser Zone.">
          <Select
            label="Zeitzone"
            labelHidden
            options={optionsWith(TIME_ZONES, form.timezone)}
            value={form.timezone}
            disabled={readOnly}
            onChange={(event) => set("timezone")(event.target.value)}
          />
        </FieldRow>
        <FieldRow label="Haushalt" hint="Freiwillig. HausMeter rechnet mit keiner der beiden Zahlen." last>
          <div style={pair}>
            <Input
              label="Wohnfläche"
              inputMode="decimal"
              placeholder="184"
              value={form.livingArea}
              disabled={readOnly}
              error={errors.livingArea}
              trailing={<span style={{ fontSize: 13, color: "var(--text-muted)" }}>m²</span>}
              onChange={(event) => set("livingArea")(event.target.value)}
            />
            <Input
              label="Personen"
              inputMode="numeric"
              placeholder="4"
              value={form.household}
              disabled={readOnly}
              error={errors.household}
              onChange={(event) => set("household")(event.target.value)}
            />
          </div>
        </FieldRow>
      </SettingsSection>

      <SettingsSection title="Anschrift" subtitle="Freiwillig. HausMeter misst auch ohne zu wissen, wo es steht.">
        <FieldRow label="Straße und Hausnummer">
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 110px", gap: 12 }}>
            <Input label="Straße" labelHidden value={form.street} maxLength={120} disabled={readOnly} onChange={(event) => set("street")(event.target.value)} />
            <Input label="Hausnummer" labelHidden value={form.houseNumber} maxLength={20} disabled={readOnly} onChange={(event) => set("houseNumber")(event.target.value)} />
          </div>
          {/* Eigenes Feld und nicht an die Straße gehängt: Sonst stünde der Zusatz
              im Umschalter mitten im Straßennamen. */}
          <Input
            label="Adresszusatz"
            labelHidden
            placeholder="Adresszusatz, z. B. Hinterhaus, 2. OG"
            value={form.addition}
            maxLength={120}
            disabled={readOnly}
            style={{ marginTop: 10 }}
            onChange={(event) => set("addition")(event.target.value)}
          />
        </FieldRow>
        <FieldRow label="Ort">
          <div style={{ display: "grid", gridTemplateColumns: "110px minmax(0,1fr)", gap: 12 }}>
            <Input label="Postleitzahl" labelHidden value={form.postalCode} maxLength={20} disabled={readOnly} onChange={(event) => set("postalCode")(event.target.value)} />
            <Input label="Ort" labelHidden value={form.city} maxLength={120} disabled={readOnly} onChange={(event) => set("city")(event.target.value)} />
          </div>
        </FieldRow>
        <FieldRow label="Land">
          <Select
            label="Land"
            labelHidden
            options={[{ value: "", label: "Keine Angabe" }, ...optionsWith(COUNTRIES, form.country.toUpperCase())]}
            value={form.country.toUpperCase()}
            disabled={readOnly}
            onChange={(event) => set("country")(event.target.value)}
          />
        </FieldRow>
        <FieldRow label="Geokoordinaten" hint={coordinates === null ? "Noch nicht hinterlegt." : null} last>
          {showCoordinateFields ? (
            <div style={pair}>
              <Input
                label="Breitengrad"
                inputMode="decimal"
                placeholder="52.1416"
                value={form.latitude}
                disabled={readOnly}
                error={errors.latitude}
                onChange={(event) => set("latitude")(event.target.value)}
              />
              <Input
                label="Längengrad"
                inputMode="decimal"
                placeholder="8.5583"
                value={form.longitude}
                disabled={readOnly}
                error={errors.longitude}
                onChange={(event) => set("longitude")(event.target.value)}
              />
            </div>
          ) : readOnly ? (
            coordinates !== null && (
              <span style={{ display: "inline-flex", alignItems: "center", height: 38, fontFamily: "var(--font-mono)", fontSize: 13.5, color: "var(--text-strong)" }}>
                {coordinates}
              </span>
            )
          ) : (
            <button
              type="button"
              onClick={() => setGeoOpen(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 38,
                padding: 0,
                border: 0,
                background: "transparent",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: 13.5,
                fontWeight: 600,
                color: "var(--hm-green-700)",
              }}
            >
              {coordinates !== null ? (
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-strong)", fontWeight: 500 }}>{coordinates}</span>
              ) : (
                "Koordinaten eingeben"
              )}
              <icons.IconChevronRight size={16} />
            </button>
          )}
        </FieldRow>
      </SettingsSection>

      {rights.canManage ? <SiteSharesSection siteId={site.id} timeZone={site.timezone} /> : <SharedWithMeSection siteId={site.id} />}

      <SettingsSection title="Geräte">
        <button
          type="button"
          onClick={onOpenDevices}
          disabled={onOpenDevices === undefined}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            width: "100%",
            padding: "16px 20px",
            border: 0,
            background: "transparent",
            cursor: onOpenDevices === undefined ? "default" : "pointer",
            textAlign: "left",
            fontFamily: "var(--font-body)",
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }}>
              {devices.isSuccess ? deviceLabel(deviceCount) : "Wird geladen …"}
            </span>
            <span style={{ display: "block", fontSize: 12.5, color: "var(--text-muted)" }}>
              Zähler, Wechselrichter und Sensoren dieser Anlage
            </span>
          </span>
          {onOpenDevices !== undefined && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 600, color: "var(--hm-green-700)", whiteSpace: "nowrap" }}>
              Geräteverwaltung
              <icons.IconChevronRight size={16} />
            </span>
          )}
        </button>
      </SettingsSection>

      {rights.canDelete && <ArchiveSection site={site} onError={setError} />}

      {error !== null && <Alert tone="danger">{error}</Alert>}

      {(dirty || toast !== null) && (
        <div
          role="status"
          style={{
            position: "sticky",
            bottom: 16,
            zIndex: 5,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            padding: "10px 12px 10px 18px",
            borderRadius: "var(--radius-lg)",
            background: "var(--surface-card)",
            border: "1px solid var(--border-default)",
            boxShadow: "var(--shadow-lg)",
          }}
        >
          <span style={{ flex: "1 1 180px", fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }}>
            {dirty ? "Ungespeicherte Änderungen" : toast}
          </span>
          {dirty && (
            <span style={{ display: "flex", gap: 8 }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setForm(saved);
                  setGeoOpen(false);
                  setError(null);
                }}
              >
                Verwerfen
              </Button>
              <Button size="sm" disabled={patch === null || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? "Wird gespeichert …" : "Speichern"}
              </Button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Archivieren — die einzige Handlung dieser Seite, die etwas wegnimmt.
 *
 * **Gelöscht wird hier nichts.** An der Anlage hängen Zähler und deren
 * Verbrauchsdaten; sie sind die Grundlage jeder Abrechnung. Die Anlage
 * verschwindet aus dem Umschalter, gibt ihren Platz im Kontingent frei, und wer
 * sie geteilt bekommen hat, sieht sie nicht mehr. „Später wiederherstellen" aus
 * dem Entwurf steht nicht da: Den Weg gibt es nicht.
 */
function ArchiveSection({ site, onError }: { site: Site; onError: (message: string) => void }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const archived = site.status === "archived";

  const archive = useMutation({
    mutationFn: async () => {
      const { error: failed } = await api.DELETE("/sites/{siteId}", { params: { path: { siteId: site.id } } });
      if (failed) throw new Error(apiErrorMessage(failed) ?? "Archivieren fehlgeschlagen.");
    },
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
    onError: (cause: Error) => {
      setConfirming(false);
      onError(cause.message);
    },
  });

  return (
    <SettingsSection title="Anlage verwalten">
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", padding: "16px 20px" }}>
        <span style={{ flex: "1 1 300px", display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: archived ? "var(--text-strong)" : "var(--hm-danger)" }}>
            {archived ? "Diese Anlage ist archiviert" : "Anlage archivieren"}
          </span>
          <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
            {archived
              ? "Sie nimmt keine neuen Werte mehr auf und zählt nicht gegen dein Kontingent; ihre bisherigen Daten bleiben erhalten."
              : "Die Anlage verschwindet aus dem Umschalter und gibt ihren Platz im Kontingent frei. Gelöscht wird nichts — Zähler und Verbrauchsdaten bleiben erhalten."}
          </span>
        </span>
        {archived ? (
          <Badge>archiviert</Badge>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
            Archivieren …
          </Button>
        )}
      </div>

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`„${site.name}" archivieren?`}
        description="Wer die Anlage geteilt bekommen hat, sieht sie danach nicht mehr. Zähler und Verbrauchsdaten bleiben erhalten."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Abbrechen
            </Button>
            <Button variant="danger" disabled={archive.isPending} onClick={() => archive.mutate()}>
              Archivieren
            </Button>
          </>
        }
      >
        {null}
      </Dialog>
    </SettingsSection>
  );
}

/** „3 Geräte verbunden" — und bei null keine Null, sondern ein Satz. */
function deviceLabel(count: number): string {
  if (count === 0) return "Noch kein Gerät verbunden";
  return count === 1 ? "Ein Gerät verbunden" : `${count} Geräte verbunden`;
}
